import * as React from 'react';
import { Icon } from '../icons.jsx';
import { Button, Badge, Tag, ProviderPhoto, Field, Input, Toggle, ProgressBar } from '../ui.jsx';
import { AGE_GROUPS, AGE_GROUP_RANGES, AGE_GROUP_INITIALS } from '../data';
import { api } from '../api';
import { useApi, useAsyncAction, Skeleton, ErrorState } from '../hooks';
import { ClassroomSetup } from './classroom-setup.jsx';

// Provider Dashboard (logged-in)

const SidebarItem = ({ icon, label, active, badge, onClick }) => (
  <button
    onClick={onClick}
    aria-current={active ? 'page' : undefined}
    style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 12px', borderRadius: 10,
      background: active ? 'var(--brand-tint)' : 'transparent',
      color: active ? 'var(--brand-ink)' : 'var(--ink-2)',
      border: 'none', cursor: 'pointer', width: '100%',
      textAlign: 'left', fontFamily: 'inherit',
      fontSize: 14, fontWeight: active ? 700 : 500,
      transition: 'all .12s ease',
    }}
  >
    <Icon name={icon} size={16} style={{ color: active ? 'var(--brand)' : 'var(--ink-3)' }}/>
    <span style={{ flex: 1 }}>{label}</span>
    {badge && (
      <span style={{
        background: active ? 'var(--brand)' : 'var(--coral)',
        color: '#fff', fontSize: 11, fontWeight: 700,
        padding: '2px 7px', borderRadius: 999, minWidth: 18, textAlign: 'center',
      }}>{badge}</span>
    )}
  </button>
);

const AgeToggleRow = ({ group, info, value, onChange }) => (
  <div className="age-row" style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 20px', borderBottom: '1px solid var(--line)',
    gap: 16,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
      <span style={{
        width: 40, height: 40, borderRadius: 10,
        background: 'var(--bg-2)', color: 'var(--ink-2)',
        display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 13,
        flexShrink: 0,
      }}>
        {AGE_GROUP_INITIALS[group] || group[0]}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
          {group} <span style={{ fontWeight: 500, color: 'var(--ink-3)', fontSize: 13 }}>· {AGE_GROUP_RANGES[group]}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{info.note}</div>
      </div>
    </div>

    <div className="age-row-controls" style={{ display: 'flex', alignItems: 'center', gap: 18, flexShrink: 0 }}>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Open spots
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'var(--surface)', border: '1px solid var(--line-2)',
          borderRadius: 999, padding: '4px 4px 4px 14px', marginTop: 6,
        }}>
          <strong style={{ fontSize: 15, color: 'var(--ink)' }}>{info.count}</strong>
          <button aria-label={`Decrease ${group} open spots`} onClick={() => onChange('count', Math.max(0, info.count - 1))} style={{
            width: 26, height: 26, borderRadius: 999, background: 'var(--bg-2)',
            border: 'none', cursor: 'pointer', color: 'var(--ink-2)', display: 'grid', placeItems: 'center', fontFamily: 'inherit',
          }}>−</button>
          <button aria-label={`Increase ${group} open spots`} onClick={() => onChange('count', info.count + 1)} style={{
            width: 26, height: 26, borderRadius: 999, background: 'var(--bg-2)',
            border: 'none', cursor: 'pointer', color: 'var(--ink-2)', display: 'grid', placeItems: 'center', fontFamily: 'inherit',
          }}>+</button>
        </div>
      </div>
      <Toggle value={value} onChange={(v) => onChange('on', v)} label={value ? 'Accepting' : 'Paused'}/>
    </div>
  </div>
);

const TourRow = ({ booking, onConfirm }) => {
  const pending = booking.status === 'pending';
  return (
    <div className="tour-row">
      <div className="tour-family">
        <span>{booking.parentName.split(/\s+/).map(part => part[0]).slice(0, 2).join('')}</span>
        <div>
          <strong>{booking.parentName}</strong>
          <small>{booking.phone || 'Phone not provided'}</small>
        </div>
      </div>
      <Tag>{booking.childAge}</Tag>
      <span className="tour-type">
        <Icon
          name={booking.tourType === 'virtual' ? 'video' : 'calendar-check'}
          size={14}
        />
        {booking.tourType === 'virtual' ? 'Virtual' : 'In person'}
      </span>
      <span className="tour-when">{booking.date} at {booking.time}</span>
      <Badge tone={pending ? 'coral' : 'open'} icon={pending ? undefined : 'check'}>
        {pending ? 'Waiting' : 'Confirmed'}
      </Badge>
      <div className="tour-actions">
        {pending
          ? <Button size="sm" variant="primary" onClick={() => onConfirm(booking.id)}>
              Confirm
            </Button>
          : <Button size="sm" variant="quiet">Details</Button>}
      </div>
    </div>
  );
};

const ToursView = ({ bookings, loading, error, reload, onConfirm, confirmError }) => (
  <div className="tours-view">
    <div className="tours-heading">
      <div>
        <strong>Tour requests</strong>
        <span>Requests from family profiles and search results appear here.</span>
      </div>
      <Badge tone="neutral">{bookings.length} total</Badge>
    </div>
    {(error || confirmError) && (
      <div className="tour-error" role="alert">
        {confirmError
          ? `Couldn’t confirm the tour: ${confirmError.message}`
          : `Couldn’t load tour requests: ${error.message}`}
        <button type="button" onClick={reload}>Try again</button>
      </div>
    )}
    {loading
      ? <div className="tour-loading">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} height={64} radius={10}/>
          ))}
        </div>
      : bookings.length
        ? bookings.map(booking => (
            <TourRow key={booking.id} booking={booking} onConfirm={onConfirm}/>
          ))
        : <div className="tour-empty">
            <Icon name="calendar-check" size={22}/>
            <strong>No tour requests yet</strong>
            <span>New family requests will appear here automatically.</span>
          </div>}
  </div>
);

const StatCard = ({ label, value, sub, icon, tone }) => (
  <div style={{
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-md)',
    padding: 20,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{
        width: 30, height: 30, borderRadius: 8,
        background: tone === 'coral' ? 'var(--coral-soft)' : 'var(--brand-tint)',
        color: tone === 'coral' ? 'var(--coral)' : 'var(--brand)',
        display: 'grid', placeItems: 'center',
      }}>
        <Icon name={icon} size={15}/>
      </span>
    </div>
    <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.02em', lineHeight: 1 }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6 }}>{sub}</div>}
  </div>
);

const WaitlistView = ({
  entries,
  loading,
  error,
  reload,
  onContact,
  onPlace,
  actionError,
}) => (
  <div style={{
    background: 'var(--surface)', border: '1px solid var(--line)',
    borderRadius: 'var(--r-lg)', overflow: 'hidden',
  }}>
    <div style={{
      padding: '18px 20px', borderBottom: '1px solid var(--line)',
      background: 'var(--surface-2)',
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>Current waitlist</div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 3 }}>
        Families are shown in the order they joined.
      </div>
    </div>
    {error && <ErrorState error={error} onRetry={reload}/>}
    {actionError && (
      <div className="waitlist-action-error" role="alert">
        Couldn’t update this family: {actionError.message}
      </div>
    )}
    {loading
      ? <div style={{ padding: 20, display: 'grid', gap: 10 }}>
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={58} radius={10}/>)}
        </div>
      : entries.map((entry, index) => (
          <div className="waitlist-row" key={entry.id}>
            <span className="waitlist-position">{index + 1}</span>
            <div>
              <strong>{entry.parentName}</strong>
              <span>{entry.phone}</span>
            </div>
            <Tag>{entry.childAge}</Tag>
            <Badge tone={entry.status === 'waiting' ? 'wait' : 'open'}>
              {entry.status === 'waiting'
                ? 'Waiting'
                : entry.status === 'contacted'
                  ? 'Contacted'
                  : 'Placed'}
            </Badge>
            {entry.status === 'waiting'
              ? <Button variant="secondary" size="sm" onClick={() => onContact(entry.id)}>
                  Contact
                </Button>
              : entry.status === 'contacted'
                ? <Button variant="secondary" size="sm" onClick={() => onPlace(entry.id)}>
                    Mark placed
                  </Button>
                : <span className="waitlist-contacted-at">Complete</span>}
          </div>
        ))}
  </div>
);

export const DashboardScreen = ({ go }) => {
  const providerId = 'sunnyside'; // prototype: signed-in provider hardcoded
  const [tab, setTab] = React.useState('dashboard');
  const [ages, setAges] = React.useState({
    'Infant':         { on: true,  count: 2, note: 'Two openings starting June 2' },
    'Young Toddler':  { on: true,  count: 1, note: 'One opening starting July 1' },
    'Older Toddler':  { on: false, count: 0, note: 'Waitlist · 2 families ahead' },
    'Pre-K':          { on: false, count: 0, note: 'Waitlist · 4 families ahead' },
    'School Age':     { on: false, count: 0, note: 'Not currently offered' },
  });
  const [lastUpdated, setLastUpdated] = React.useState('Just now');
  const [stale, setStale] = React.useState(false);

  const {
    data: bookingData,
    loading: bookingLoading,
    error: bookingError,
    reload: reloadBookings,
  } = useApi(
    () => api.bookings.list(providerId),
    [providerId],
  );
  const [bookings, setBookings] = React.useState([]);
  React.useEffect(() => { if (bookingData) setBookings(bookingData); }, [bookingData]);
  const {
    data: waitlistEntries,
    loading: waitlistLoading,
    error: waitlistError,
    reload: reloadWaitlist,
  } = useApi(() => api.waitlists.list(providerId), [providerId]);

  const confirmBookingAction = useAsyncAction(api.bookings.confirm);
  const pushAvailability = useAsyncAction(api.providers.updateAvailability);
  const contactWaitlistAction = useAsyncAction(api.waitlists.contact);
  const placeWaitlistAction = useAsyncAction(api.waitlists.place);

  const updateAge = (g, k, v) => {
    setAges(prev => ({ ...prev, [g]: { ...prev[g], [k]: v } }));
    setStale(true);
  };
  const syncAgesFromProvider = (provider) => {
    setAges(Object.fromEntries(
      AGE_GROUPS.map((ageGroup) => [
        ageGroup,
        {
          on: provider.availability[ageGroup].spots > 0,
          count: provider.availability[ageGroup].spots,
          note: provider.availability[ageGroup].note,
        },
      ]),
    ));
    setLastUpdated('Just now');
    setStale(false);
  };

  const totalOpen = Object.values(ages).filter(a => a.on).reduce((s, a) => s + a.count, 0);
  const pendingTours = bookings.filter(booking => booking.status === 'pending').length;
  const waitingCount = (waitlistEntries || []).filter(entry => entry.status === 'waiting').length;

  const confirmBooking = async (id) => {
    setBookings(current =>
      current.map(booking =>
        booking.id === id ? { ...booking, status: 'confirmed' } : booking,
      ),
    );
    try {
      await confirmBookingAction.run(providerId, id);
    } catch {
      reloadBookings();
    }
  };

  const pushNow = async () => {
    try {
      await pushAvailability.run(providerId, ages);
      setLastUpdated('Just now');
      setStale(false);
    } catch {
      // Leave stale flag set so the user can retry.
    }
  };

  const contactWaitlist = async (entryId) => {
    try {
      await contactWaitlistAction.run(providerId, entryId);
      reloadWaitlist();
    } catch {
      // useAsyncAction exposes the error in the waitlist view.
    }
  };

  const placeWaitlist = async (entryId) => {
    try {
      await placeWaitlistAction.run(providerId, entryId);
      reloadWaitlist();
    } catch {
      // useAsyncAction exposes the error in the waitlist view.
    }
  };

  return (
    <div className="dash-shell" style={{ minHeight: '100vh', background: 'var(--bg)', display: 'grid', gridTemplateColumns: '260px 1fr' }}>
      {/* SIDEBAR ============================================ */}
      <aside className="dash-sidebar" style={{
        background: 'var(--surface)',
        borderRight: '1px solid var(--line)',
        padding: '20px 14px',
        position: 'sticky', top: 0, height: '100vh',
        display: 'flex', flexDirection: 'column',
      }}>
        <button
          onClick={() => go('home')}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '6px 10px', marginBottom: 22,
          }}
        >
          <img
            src="/assets/logo.png" alt=""
            loading="lazy"
            style={{ width: 28, height: 34, objectFit: 'contain', display: 'block' }}
          />
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
            Find My Childcare
          </span>
        </button>

        <div style={{ padding: '0 10px 16px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            Your center
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>Sunnyside Learning Garden</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Fairmount · Philadelphia</div>
        </div>

        <nav style={{ display: 'grid', gap: 2, padding: '12px 0' }}>
          <SidebarItem icon="grid"            label="Dashboard"   active={tab === 'dashboard'}    onClick={() => setTab('dashboard')}/>
          <SidebarItem icon="calendar-check"  label="Availability" active={tab === 'availability'} onClick={() => setTab('availability')}/>
          <SidebarItem icon="home"            label="Classrooms"   active={tab === 'classrooms'}   onClick={() => setTab('classrooms')}/>
          <SidebarItem icon="spark"           label="Text assistant" active={false} onClick={() => go('assistant')}/>
          <SidebarItem icon="home"            label="Profile"      active={tab === 'profile'}     onClick={() => setTab('profile')}/>
          <SidebarItem icon="calendar-check"  label="Tours"        active={tab === 'tours'}       onClick={() => setTab('tours')} badge={pendingTours || null}/>
          <SidebarItem icon="user"            label="Waitlist"     active={tab === 'waitlist'}    onClick={() => setTab('waitlist')} badge={waitingCount || null}/>
        </nav>

        <div style={{ marginTop: 'auto', padding: 12, background: 'var(--brand-tint)', borderRadius: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-ink)', marginBottom: 4 }}>
            Prototype plan
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>
            Working price: $65/month
          </div>
          <Button variant="secondary" size="sm" full>Billing preview</Button>
        </div>
      </aside>

      <div className="dash-mobile-topbar" style={{
        position: 'sticky', top: 0, zIndex: 45,
        flexDirection: 'column', alignItems: 'stretch', gap: 10,
        padding: '12px 16px', overflow: 'hidden',
        background: 'rgba(255,252,245,0.92)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid var(--line)',
      }}>
        <button
          className="dash-mobile-brand"
          onClick={() => go('home')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            flexShrink: 0, background: 'transparent', border: 'none',
            color: 'var(--ink)', fontWeight: 900, cursor: 'pointer', padding: '8px 4px',
          }}
        >
          <img src="/assets/logo.png" alt="" loading="lazy" style={{ width: 23, height: 28, objectFit: 'contain' }}/>
          Find My Childcare
        </button>
        <div className="dash-mobile-tabs">
          {[
            ['dashboard', 'Dashboard'],
            ['availability', 'Availability'],
            ['classrooms', 'Classrooms'],
            ['assistant', 'Assistant'],
            ['profile', 'Profile'],
            ['tours', 'Tours'],
            ['waitlist', 'Waitlist'],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => id === 'assistant' ? go('assistant') : setTab(id)}
              aria-current={tab === id ? 'page' : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center',
                border: '1px solid ' + (tab === id ? 'var(--brand)' : 'var(--line-2)'),
                background: tab === id ? 'var(--brand)' : 'var(--surface)',
                color: tab === id ? '#fff' : 'var(--ink-2)',
                borderRadius: 999, padding: '10px 12px', fontWeight: 800, cursor: 'pointer',
                boxShadow: tab === id ? '0 12px 26px -18px rgba(26,102,213,0.75)' : 'var(--shadow-sm)',
              }}
            >{label}{id === 'tours' && pendingTours ? ` · ${pendingTours}` : ''}</button>
          ))}
        </div>
      </div>

      {/* MAIN =============================================== */}
      <section className="dash-main" style={{ padding: '28px 36px', minWidth: 0 }}>
        {/* Top bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 28, flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <h1 style={{
              fontSize: 28, fontWeight: 700, color: 'var(--ink)',
              letterSpacing: '-0.02em',
            }}>
              {tab === 'dashboard'    && 'Welcome back, Lena'}
              {tab === 'availability' && 'Availability'}
              {tab === 'classrooms'    && 'Classrooms'}
              {tab === 'profile'      && 'Center profile'}
              {tab === 'tours'        && 'Tours'}
              {tab === 'waitlist'     && 'Waitlist'}
            </h1>
            <p style={{ color: 'var(--ink-3)', fontSize: 14, marginTop: 4 }}>
              {tab === 'dashboard'    && 'Monday, May 18 · ' + (totalOpen ? `${totalOpen} live spots showing to families` : 'No live spots, families won\u2019t see your center')}
              {tab === 'availability' && 'Toggle openings per classroom. Changes go live in under a minute.'}
              {tab === 'classrooms'    && 'Set room capacity, enrollment, openings, and waitlist pressure.'}
              {tab === 'profile'      && 'Edit what families see on your public page.'}
              {tab === 'tours'        && `${pendingTours} waiting · ${bookings.length} total`}
              {tab === 'waitlist'     && `${waitingCount} waiting · ${waitlistEntries?.length || 0} total`}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button variant="quiet" icon="bell" size="md" aria-label="Notifications"/>
            <Button variant="secondary" icon="spark" onClick={() => go('assistant')}>Text assistant</Button>
            <Button variant="secondary" icon="edit">Edit profile</Button>
          </div>
        </div>

        {tab === 'dashboard' && (
          <DashboardHome
            ages={ages} updateAge={updateAge}
            bookings={bookings} confirmBooking={confirmBooking}
            totalOpen={totalOpen}
            lastUpdated={lastUpdated} stale={stale} pushNow={pushNow}
            setStale={setStale}
            pushError={pushAvailability.error}
            confirmError={confirmBookingAction.error}
          />
        )}

        {tab === 'availability' && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--line)',
            borderRadius: 'var(--r-lg)', overflow: 'hidden',
          }}>
            {AGE_GROUPS.map(g => (
              <AgeToggleRow
                key={g} group={g} info={ages[g]} value={ages[g].on}
                onChange={(k, v) => updateAge(g, k, v)}
              />
            ))}
          </div>
        )}

        {tab === 'classrooms' && (
          <ClassroomSetup providerId={providerId} onProviderChange={syncAgesFromProvider}/>
        )}

        {tab === 'tours' && (
          <ToursView
            bookings={bookings}
            loading={bookingLoading}
            error={bookingError}
            reload={reloadBookings}
            onConfirm={confirmBooking}
            confirmError={confirmBookingAction.error}
          />
        )}

        {tab === 'profile' && (
          <ProfileEditor providerId={providerId}/>
        )}
        {tab === 'waitlist' && (
          <WaitlistView
            entries={waitlistEntries || []}
            loading={waitlistLoading}
            error={waitlistError}
            reload={reloadWaitlist}
            onContact={contactWaitlist}
            onPlace={placeWaitlist}
            actionError={contactWaitlistAction.error || placeWaitlistAction.error}
          />
        )}
      </section>
    </div>
  );
};

// ====== Dashboard home (default tab body) ======
const DashboardHome = ({ ages, updateAge, bookings, confirmBooking, totalOpen, lastUpdated, stale, pushNow, setStale, pushError, confirmError }) => {
  const pendingTours = bookings.filter(booking => booking.status === 'pending').length;
  const confirmedTours = bookings.length - pendingTours;
  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {/* Stale warning */}
      {stale && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderRadius: 'var(--r-md)', gap: 16,
          background: 'var(--wait-soft)', border: '1px solid rgba(176,122,28,0.25)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#7A5210' }}>
            <Icon name="bell" size={18}/>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Your availability hasn&rsquo;t been updated in 8 days</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>Families trust live counts, refresh now so you keep showing up first.</div>
            </div>
          </div>
          <Button variant="primary" size="sm" onClick={pushNow}>Update now</Button>
        </div>
      )}

      {/* Stats row */}
      {totalOpen === 0 && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '16px 18px', borderRadius: 'var(--r-md)',
          background: 'var(--coral-soft)', border: '1px solid rgba(226,50,38,0.22)',
          color: '#8E241C',
        }}>
          <Icon name="bell" size={18} style={{ marginTop: 1, flexShrink: 0 }}/>
          <div>
            <div style={{ fontWeight: 900, fontSize: 14 }}>No live spots. Families can’t see your center right now.</div>
            <div style={{ fontSize: 13, marginTop: 2 }}>Toggle a classroom on below to start showing up in search.</div>
          </div>
        </div>
      )}
      <div className="dash-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        <StatCard label="Open spots live" value={totalOpen} sub="Showing to families now" icon="spark"/>
        <StatCard label="Tours awaiting reply" value={pendingTours} sub="Families waiting for confirmation" icon="inbox" tone="coral"/>
        <StatCard label="Profile analytics" value="—" sub="Available after launch" icon="user"/>
        <StatCard label="Tours requested" value={bookings.length} sub={`${confirmedTours} confirmed · ${pendingTours} pending`} icon="calendar-check"/>
      </div>

      {/* Profile completion + last updated */}
      <div className="dash-twocol" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }}>
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--line)',
          borderRadius: 'var(--r-md)', padding: 22,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>Complete your profile</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                Complete profiles give families more information to compare.
              </div>
            </div>
            <Button variant="secondary" size="sm" iconRight="arrow-right">Continue</Button>
          </div>
          <ProgressBar value={75} label="Profile completion"/>
          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            {[
              { done: true,  label: 'Basic info' },
              { done: true,  label: 'Hours & licensing' },
              { done: true,  label: 'Description' },
              { done: false, label: 'Add 3+ photos' },
            ].map(s => (
              <span key={s.label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, color: s.done ? 'var(--ink-3)' : 'var(--coral)',
                fontWeight: 500,
              }}>
                <Icon name={s.done ? 'check-circle' : 'plus'} size={14}/>
                {s.label}
              </span>
            ))}
          </div>
        </div>

        <div style={{
          background: 'var(--surface)', border: '1px solid var(--line)',
          borderRadius: 'var(--r-md)', padding: 22,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
            Availability last updated
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.02em', marginBottom: 4 }}>
            {lastUpdated}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
            We&rsquo;ll nudge you after 7 days of inactivity.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="primary" size="sm" icon="spark" onClick={pushNow}>Refresh now</Button>
            <Button variant="quiet" size="sm" onClick={() => { setStale(true); }}>Preview stale state</Button>
          </div>
          {pushError && (
            <div role="alert" style={{
              marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 13,
              color: '#7A1F18', background: 'var(--coral-soft)', border: '1px solid rgba(226,50,38,0.2)',
            }}>
              Update didn&rsquo;t save: {pushError.message}. Your changes are still local &mdash; click Refresh now to retry.
            </div>
          )}
        </div>
      </div>

      {/* Availability quick-toggle */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--line)',
        borderRadius: 'var(--r-lg)', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>Openings by classroom</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Toggle a room off the instant it fills.</div>
          </div>
          <Button variant="ghost" size="sm" iconRight="chev-right">See availability</Button>
        </div>
        {AGE_GROUPS.map(g => (
          <AgeToggleRow
            key={g} group={g} info={ages[g]} value={ages[g].on}
            onChange={(k, v) => updateAge(g, k, v)}
          />
        ))}
      </div>

      <ToursView
        bookings={bookings}
        loading={false}
        error={null}
        reload={() => {}}
        onConfirm={confirmBooking}
        confirmError={confirmError}
      />
    </div>
  );
};

const ProfileEditor = ({ providerId }) => {
  const { data: provider, loading, error, reload } = useApi(
    () => api.providers.get(providerId),
    [providerId],
  );
  const [form, setForm] = React.useState({
    description: '',
    hours: '',
    languages: '',
  });
  const [saved, setSaved] = React.useState(false);
  const saveAction = useAsyncAction(api.providers.updateProfile);

  React.useEffect(() => {
    if (!provider) return;
    setForm({
      description: provider.description,
      hours: provider.hours,
      languages: provider.languages,
    });
  }, [provider]);

  const change = (field, value) => {
    setSaved(false);
    setForm(current => ({ ...current, [field]: value }));
  };

  const reset = () => {
    if (!provider) return;
    setForm({
      description: provider.description,
      hours: provider.hours,
      languages: provider.languages,
    });
    setSaved(false);
  };

  const save = async () => {
    try {
      await saveAction.run(providerId, form);
      setSaved(true);
    } catch {
      // useAsyncAction exposes the error in the form.
    }
  };

  if (loading) return <Skeleton height={460} radius={18}/>;
  if (error || !provider) {
    return <ErrorState error={error || new Error('Provider not found')} onRetry={reload}/>;
  }

  return (
    <div className="profile-editor-card" style={{
      background: 'var(--surface)', border: '1px solid var(--line)',
      borderRadius: 'var(--r-lg)', padding: 32, display: 'grid', gap: 22,
      minWidth: 0,
    }}>
      <div className="profile-official-field">
        <span>Official provider name</span>
        <strong>{provider.name}</strong>
        <small>Matched to the Pennsylvania provider record. Contact support to correct it.</small>
      </div>
      <Field
        id="profile-description"
        label="Public description"
        hint={`${form.description.length}/320 characters · shown on your public profile`}
        style={{ minWidth: 0 }}
      >
        <textarea
          className="profile-editor-textarea"
          value={form.description}
          maxLength={320}
          onChange={event => change('description', event.target.value)}
        />
      </Field>
      <div className="profile-editor-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, minWidth: 0 }}>
        <Field id="profile-hours" label="Regular hours" style={{ minWidth: 0 }}>
          <Input
            value={form.hours}
            onChange={event => change('hours', event.target.value)}
          />
        </Field>
        <Field id="profile-languages" label="Languages spoken" style={{ minWidth: 0 }}>
          <Input
            value={form.languages}
            onChange={event => change('languages', event.target.value)}
          />
        </Field>
      </div>
      <Field label="Photos" hint="Photo ordering is visual in this prototype" style={{ minWidth: 0 }}>
        <div className="profile-editor-photo-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, minWidth: 0 }}>
          {[{ label: 'Cover', src: provider.image }, ...(provider.gallery || []).slice(0, 2), { label: '+ add' }].map(item => (
            item.label === '+ add'
              ? <button key={item.label} type="button" style={{
                  height: 100, borderRadius: 12, border: '2px dashed var(--line-2)',
                  background: 'transparent', color: 'var(--ink-3)', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                }}>+ add photo</button>
              : <ProviderPhoto key={item.label} src={item.src} alt={`${provider.name} ${item.label.toLowerCase()}`} radius={12} height={100} label={item.label}/>
          ))}
        </div>
      </Field>
      {(saveAction.error || saved) && (
        <div className={saved ? 'profile-save-success' : 'profile-save-error'} role="status">
          {saved
            ? 'Saved. Families can now see these changes on the public profile.'
            : `Couldn’t save profile changes: ${saveAction.error.message}`}
        </div>
      )}
      <div className="profile-editor-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid var(--line)', paddingTop: 22 }}>
        <Button variant="quiet" onClick={reset}>Reset</Button>
        <Button
          variant="primary"
          icon="check"
          onClick={save}
          disabled={
            !form.description.trim() ||
            !form.hours.trim() ||
            !form.languages.trim() ||
            saveAction.loading
          }
        >
          {saveAction.loading ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
};
